import {
	AfterViewInit,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	QueryList,
	ViewChild,
	ViewChildren,
} from "@angular/core";
import { Title } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { Observable, Subject, of, takeUntil } from "rxjs";
import * as L from "leaflet";
import { CentroProcedure } from "src/app/_core/models/centros/centro-procedure.model";
import { CentrosService } from "src/app/_core/providers/centros.service";
// Models
import { CebeProcedureViewModel } from "src/app/_core/models/centros/cebe-procedure.model";
import { ContextLocationViewModel } from "src/app/_core/models/main/context-location.model";
import { LinkItem } from "src/app/_core/models/main/link-item.model";
import { UserProfileViewModel } from "src/app/_core/models/main/user-profile.model";
import { UserProfileStore } from "src/app/_core/stores/user-profile.store";

// Services
import { FormControl } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { TagViewModel } from "src/app/_core/models/home/tag.model";
import { CebesService } from "src/app/_core/providers/cebes.service";
import {
	GlobalService,
	MigaDePan,
} from "src/app/_core/providers/global.service";
import { LoaderService } from "src/app/_core/providers/loader.service";
import { getHomeMenuWithVisited } from "src/app/_core/constants/menu.constants";
import { ContractService } from "src/app/modules/contracts/services/contract.service";
import { ContratoViewModel } from "src/app/modules/contracts/interfaces/contract.interface";
import {
	AccesosService,
	TipoElementoAcceso,
} from "src/app/_core/providers/accesos.service";
import {
	HomeKpis,
	HomeKpisService,
} from "src/app/_core/providers/home-kpis.service";
import { MisResponsabilidadesService } from "src/app/_core/providers/mis-responsabilidades.service";
import { ResponsabilidadItemViewModel } from "src/app/_core/models/main/mis-responsabilidades.model";
import {
	EmpleadoLike,
	empleadoLabel,
} from "src/app/_core/utils/empleado-display";

@Component({
    selector: "app-home",
    templateUrl: "./home.page.html",
    styleUrls: ["./home.page.scss"],
    standalone: false
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
	// Constants
	private readonly TAG_TYPE_DEPARTMENT = "DEPARTMENT";
	private readonly TAG_TYPE_COUNTRY = "COUNTRY";
	private readonly TAG_TYPE_PROJECT_TYPE = "PROJECT_TYPE";

	constructor(
		private readonly _titleService: Title,
		private readonly _translateService: TranslateService,
		private readonly _userProfileStore: UserProfileStore,
		private readonly _globalService: GlobalService,
		private readonly _loaderService: LoaderService,
		private readonly _cebesService: CebesService,
		private readonly _contractService: ContractService,
		private readonly _centrosService: CentrosService,
		private readonly _accesosService: AccesosService,
		private readonly _homeKpisService: HomeKpisService,
		private readonly _misResponsabilidadesService: MisResponsabilidadesService,
		private readonly _router: Router
	) {
		const miga: MigaDePan[] = [
			{
				nombre: "header.menu.home.home",
				url: window.location.href,
			},
		];
		this._globalService.limpiarMigas();
		this._globalService.actualizarMigas(miga);
	}

	// Main
	private readonly destroy$ = new Subject<void>();
	public contextLocation: ContextLocationViewModel;
	private readonly menu: LinkItem[] = getHomeMenuWithVisited("/home");

	// Mini-mapa del banner (preview no interactivo, misma estética que el mapa global)
	@ViewChild("miniMapEl") miniMapEl?: ElementRef<HTMLDivElement>;
	private miniMap?: L.Map;
	private miniRo?: ResizeObserver;
	private miniBounds: [number, number][] | null = null;

	// Properties

	public userProfile: UserProfileViewModel;
	public departments: Array<any>;
	public countries: Array<any>;
	public projectTypes: Array<any>;

	public loadGlobe: boolean = false;
	public tagsDepartment: Array<TagViewModel>;
	public tagsCountries: Array<TagViewModel>;
	public tagsProjectType: Array<TagViewModel>;
	public selectedTags: TagViewModel[] = [];
	public selectedTagsDepartment: TagViewModel[] = [];
	public selectedTagsCountry: TagViewModel[] = [];
	public selectedTagsProyectType: TagViewModel[] = [];
	public selectedTag: TagViewModel | null;

	public filterFavouriteServices: boolean = false;
	public displayFilters: boolean = false;
	public allServices: CebeProcedureViewModel[] = [];
	public filteredServices: CebeProcedureViewModel[] = [];
	public contracts: ContratoViewModel[] = [];
	// Centros reales (lista completa de /Centros/List). Alimenta la sección "Tus centros"
	// (card → /centro/:id, "Ver todos" → /centros) y los KPIs del hero.
	public centros: CentroProcedure[] = [];
	// Último acceso por id (para la marca discreta "hace X") — recientes/visitados.
	public centroLastAccess: Record<number, string> = {};
	public contractLastAccess: Record<number, string> = {};
	public pageSize = 8;
	public page = 1;
	public reportConfig: any;

	public embedConfig: any;

	// Forms
	departmentForm = new FormControl("");
	countryForm = new FormControl("");
	projectTypeForm = new FormControl("");

	// KPIs globales SIN permisos (decisión 2026-06-11, sustituye a los derivados de
	// la lista scopeada de 2026-06-05): vienen de GET /Home/Kpis (cacheado en
	// servidor) y muestran la dimensión real de la plataforma. Países y Actividades
	// se cuentan sobre los CONTRATOS — esto arregla el "Países = 1" (antes se
	// derivaba de campos de centro que el SP no devuelve).
	public kpis: HomeKpis | null = null;
	// Medidor de actividad: 12 barras (últimos 12 meses cerrados) normalizadas.
	public actividadBarras: { label: string; pct: number; count: number }[] = [];
	public actividadTotal = 0;

	// Contratos EN CARTERA del usuario (lista scopeada completa, sin el filtro de
	// activos de la tabla). KPI del hero; null = aún cargando.
	public contratosUsuarioTotal: number | null = null;

	// ── Bloque "Tu cartera de responsable" (GET MisResponsabilidades) ──────
	// Solo se pinta si el usuario ES responsable de algo; error o vacío → oculto
	// (la home no debe degradarse por este extra).
	public respContratos: ResponsabilidadItemViewModel[] = [];
	public respCentros: ResponsabilidadItemViewModel[] = [];
	public respLoaded = false;

	get respTotal(): number {
		return this.respContratos.length + this.respCentros.length;
	}

	/** Media simple de los porcentajes de cumplimentación de toda la cartera. */
	get respMediaPct(): number {
		const todos = [...this.respContratos, ...this.respCentros];
		if (!todos.length) {
			return 0;
		}
		const suma = todos.reduce(
			(acc, it) => acc + (it.cumplimentacion?.porcentaje ?? 0),
			0,
		);
		return Math.round(suma / todos.length);
	}

	/** Entidad MÁS incompleta de la cartera, con su tipo (null si todo está al 100%). */
	get respPeorInfo(): {
		item: ResponsabilidadItemViewModel;
		esCentro: boolean;
	} | null {
		const candidatos = [
			...this.respContratos.map((item) => ({ item, esCentro: false })),
			...this.respCentros.map((item) => ({ item, esCentro: true })),
		];
		let peor: { item: ResponsabilidadItemViewModel; esCentro: boolean } | null =
			null;
		for (const c of candidatos) {
			const pct = c.item.cumplimentacion?.porcentaje ?? 100;
			if (
				pct < 100 &&
				(!peor || pct < (peor.item.cumplimentacion?.porcentaje ?? 100))
			) {
				peor = c;
			}
		}
		return peor;
	}

	get respPeor(): ResponsabilidadItemViewModel | null {
		return this.respPeorInfo?.item ?? null;
	}

	/** Tooltip del chip del peor elemento: descripción + tipo de entidad. */
	get respPeorTooltip(): string {
		const info = this.respPeorInfo;
		if (!info) {
			return "";
		}
		const tipo = this._translateService.instant(
			info.esCentro
				? "common.entityTypes.centros"
				: "common.entityTypes.contracts",
		);
		return `${info.item.descripcion || info.item.codigo} · ${tipo}`;
	}

	/** Click en el chip del peor elemento: navega a su ficha. */
	goToRespPeor(): void {
		const info = this.respPeorInfo;
		if (!info) {
			return;
		}
		this._router.navigate([
			info.esCentro ? "/sites" : "/contracts",
			info.item.id,
		]);
	}

	/** Tramo visual del porcentaje (mismos umbrales que la página de responsabilidades). */
	respPctClass(pct: number): string {
		if (pct >= 80) {
			return "is-ok";
		}
		if (pct >= 40) {
			return "is-mid";
		}
		return "is-low";
	}

	// ── Carrusel del hero (solo el bloque de copy; el panel glass no rota) ──
	// 3 mensajes estratégicos, EN ESTE ORDEN (decisión Pablo 2026-06-11):
	// contratos → centros → calidad del dato. Los textos viven en i18n bajo
	// home.hero.{s1,s2,s3}.*; aquí solo enlaces e iconos.
	public readonly heroSlides = [
		{ key: "s2", mainLink: "/contracts", secLink: "/people", secIcon: "group" },
		{ key: "s1", mainLink: "/sites", secLink: "/map", secIcon: "place" },
		{ key: "s3", mainLink: "/contracts", secLink: "/sites", secIcon: "apartment" },
	];
	public heroIndex = 0;
	// "in" = visible/entrando; "out" = saliendo (fade + desplazamiento vertical).
	public heroPhase: "in" | "out" = "in";
	public heroPaused = false;
	// Recrea la barra de progreso del dot activo (reinicia su animación CSS).
	public heroProgVisible = true;
	public readonly HERO_INTERVAL_MS = 7000;
	// prefers-reduced-motion: sin autorrotación y cambios instantáneos.
	public readonly reducedMotion =
		typeof window !== "undefined" &&
		!!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
	private heroTimer?: number;
	private heroSwapTimer?: number;
	@ViewChildren("heroDot") heroDots?: QueryList<ElementRef<HTMLButtonElement>>;

	get heroSlide() {
		return this.heroSlides[this.heroIndex];
	}

	// Imagen efectiva por centro (centro → contrato), ya como data-URI.
	public centroImg: Record<number, string> = {};
	// true cuando la lista de centros ya respondió (evita pintar 5 fantasmas
	// durante la carga inicial).
	public centrosLoaded = false;

	/** Huecos fantasma para completar el ribbon "Tus centros" hasta 5 cards. */
	get ghostSlots(): number[] {
		if (!this.centrosLoaded) {
			return [];
		}
		const visibles = Math.min(this.centros?.length ?? 0, 5);
		return Array.from({ length: 5 - visibles });
	}

	// Events
	ngOnInit(): void {
		this._globalService.setHeaderOptions(
			[],
			this.contextLocation,
			[],
			this.menu
		);

		this._titleService.setTitle(
			this._translateService.instant("title.home")
		);
		this._loaderService.start();

		this.contextLocation = {
			country: "",
			countryName: "",
			cebe: 0,
			serviceName: "",
			centro: 0,
			siteName: "",
		};

		this.userProfile = this._userProfileStore.getUserProfile();

		this._loadData();
		this._startHeroTimer();
	}

	// ── Carrusel del hero ────────────────────────────────────────────────
	// El ciclo se programa con setTimeout EN EL SWAP de cada slide — el mismo
	// instante en que se recrea la barra de progreso del dot activo — para que
	// barra y temporizador corran SIEMPRE los mismos 7 s (con setInterval la
	// barra nacía 300 ms tarde y quedaba desfasada del cambio real).
	private _startHeroTimer(): void {
		if (this.reducedMotion) {
			return; // sin autorrotación si el usuario pide menos movimiento
		}
		this._stopHeroTimer();
		this.heroTimer = window.setTimeout(
			() => this._showHeroSlide((this.heroIndex + 1) % this.heroSlides.length),
			this.HERO_INTERVAL_MS,
		);
	}

	private _stopHeroTimer(): void {
		if (this.heroTimer) {
			window.clearTimeout(this.heroTimer);
			this.heroTimer = undefined;
		}
	}

	/** Transición salida (fade/-8px) → swap de contenido → entrada (fade/+8px). */
	private _showHeroSlide(i: number): void {
		if (i === this.heroIndex) {
			return;
		}
		this._stopHeroTimer(); // sin ticks pendientes durante la transición
		if (this.reducedMotion) {
			this.heroIndex = i;
			return;
		}
		this.heroPhase = "out";
		if (this.heroSwapTimer) {
			window.clearTimeout(this.heroSwapTimer);
		}
		this.heroSwapTimer = window.setTimeout(() => {
			this.heroIndex = i; // al cambiar el índice se recrea la barra del dot
			this.heroPhase = "in";
			if (!this.heroPaused) {
				this._startHeroTimer(); // siguiente ciclo: 7 s desde AHORA, como la barra
			}
		}, 300);
	}

	/** Click en un indicador: navega; el swap reinicia temporizador y progreso. */
	goToSlide(i: number): void {
		this._showHeroSlide(i);
	}

	/** Hover sobre el hero: pausa la rotación (la barra de progreso se congela). */
	pauseHero(): void {
		this.heroPaused = true;
		this._stopHeroTimer();
	}

	/** Sale el cursor: reanuda con ciclo completo (barra reiniciada, sin desfase). */
	resumeHero(): void {
		this.heroPaused = false;
		if (this.heroPhase === "out") {
			return; // hay un swap en vuelo: él programará el siguiente ciclo
		}
		this._restartHeroProgress();
		this._startHeroTimer();
	}

	// Recrea el elemento de progreso para que su animación CSS parta de cero,
	// manteniéndolo siempre en fase con el temporizador.
	private _restartHeroProgress(): void {
		this.heroProgVisible = false;
		setTimeout(() => (this.heroProgVisible = true));
	}

	/** Flechas en los indicadores (patrón tablist): navegan y mueven el foco. */
	onHeroDotsKeydown(event: KeyboardEvent): void {
		const delta =
			event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
		if (!delta) {
			return;
		}
		event.preventDefault();
		const n = this.heroSlides.length;
		const next = (this.heroIndex + delta + n) % n;
		this.goToSlide(next);
		this.heroDots?.get(next)?.nativeElement.focus();
	}

	ngAfterViewInit(): void {
		this._initMiniMap();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this._stopHeroTimer();
		if (this.heroSwapTimer) {
			window.clearTimeout(this.heroSwapTimer);
		}
		if (this.miniRo) {
			this.miniRo.disconnect();
		}
		if (this.miniMap) {
			this.miniMap.remove();
		}
	}

	// ── Mini-mapa del banner ──────────────────────────────────────────────
	private _initMiniMap(): void {
		if (!this.miniMapEl) {
			return;
		}
		this.miniMap = L.map(this.miniMapEl.nativeElement, {
			center: L.latLng(40.0, -3.7),
			zoom: 4,
			zoomControl: false,
			attributionControl: false,
			dragging: false,
			scrollWheelZoom: false,
			doubleClickZoom: false,
			boxZoom: false,
			keyboard: false,
			touchZoom: false,
		});
		L.tileLayer(
			"https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
			{ subdomains: "abcd", maxZoom: 19 }
		).addTo(this.miniMap);

		this.miniRo = new ResizeObserver(() => {
			this.miniMap?.invalidateSize();
			this._miniFit();
		});
		this.miniRo.observe(this.miniMapEl.nativeElement);

		this._loadMiniCentros();
	}

	private _miniParseCoord(v: string | null | undefined): number {
		if (v == null) {
			return NaN;
		}
		return parseFloat(String(v).trim().replace(",", "."));
	}

	private _loadMiniCentros(): void {
		this._centrosService
			.getCentros()
			.pipe(takeUntil(this.destroy$))
			.subscribe((data) => {
				if (!this.miniMap) {
					return;
				}
				const layer = L.layerGroup().addTo(this.miniMap);
				const bounds: [number, number][] = [];
				for (const c of data ?? []) {
					const lat = this._miniParseCoord(c.latitud);
					const lng = this._miniParseCoord(c.longitud);
					if (
						isNaN(lat) ||
						isNaN(lng) ||
						lat < -90 ||
						lat > 90 ||
						lng < -180 ||
						lng > 180 ||
						(lat === 0 && lng === 0)
					) {
						continue;
					}
					L.marker([lat, lng], {
						interactive: false,
						keyboard: false,
						icon: L.divIcon({
							className: "mini-pin-wrap",
							html: `<span class="mini-pin ${(c as CentroProcedure).activo ? "prod" : "noprod"}"></span>`,
							iconSize: [10, 10],
							iconAnchor: [5, 5],
						}),
					}).addTo(layer);
					bounds.push([lat, lng]);
				}
				this.miniBounds = bounds;
				this._miniFit();
			});
	}

	private _miniFit(): void {
		if (!this.miniMap || !this.miniBounds?.length) {
			return;
		}
		const s = this.miniMap.getSize();
		if (s.x > 0 && s.y > 0) {
			this.miniMap.fitBounds(L.latLngBounds(this.miniBounds), {
				padding: [24, 24],
				maxZoom: 7,
			});
			this.miniBounds = null;
		}
	}

	// Public methods
	toggleFilters() {
		this.displayFilters = !this.displayFilters;

		if (this.displayFilters == true) {
			this._loadSelectores();
			this._refreshFilter();
		} else {
			this.departmentForm.reset();
			this.countryForm.reset();
			this.projectTypeForm.reset();
		}
	}

	departmentSelectionChanged(event) {
		let newTags: string[] = [];

		if (event && event.value && event.value.length > 0) {
			newTags = event.value;
		}

		this._refreshTagList(newTags, this.TAG_TYPE_DEPARTMENT);
	}

	countrySelectionChanged(event) {
		let newTags: string[] = [];

		if (event && event.value && event.value.length > 0) {
			newTags = event.value;
		}

		this._refreshTagList(newTags, this.TAG_TYPE_COUNTRY);
	}

	projectTypeSelectionChanged(event) {
		let newTags: string[] = [];

		if (event && event.value && event.value.length > 0) {
			newTags = event.value;
		}

		this._refreshTagList(newTags, this.TAG_TYPE_PROJECT_TYPE);
	}

	favCebe(cebe: string) {
		this._refreshFilter();
	}

	unfavCebe(cebe: string) {
		this._refreshFilter();
	}

	loadMore() {
		this.page++;
	}

	getTagName(tag: TagViewModel) {
		switch (tag.tagType) {
			case this.TAG_TYPE_DEPARTMENT:
				return `departments.${  tag.tag}`;
			case this.TAG_TYPE_COUNTRY:
				return `countries.${  tag.tag}`;
			case this.TAG_TYPE_PROJECT_TYPE:
				return tag.tag;
			default:
				return tag.tag;
		}
	}

	toggleFavouritesServices() {
		this.filterFavouriteServices = !this.filterFavouriteServices;
		this._refreshFilter();
	}

	selectTag(tag) {
		const coincidentTag = this.selectedTags.some(
			(x) => x.tag == tag.tag && x.tagType == tag.tagType
		);

		if (!coincidentTag) {
			switch (tag.tagType) {
				case this.TAG_TYPE_DEPARTMENT:
					this.selectedTagsDepartment.push(tag);
					this.selectedTag = null;
					this._refreshFilter();

					break;
				case this.TAG_TYPE_COUNTRY:
					this.selectedTagsCountry.push(tag);
					this.selectedTag = null;
					this._refreshFilter();

					break;
				case this.TAG_TYPE_PROJECT_TYPE:
					this.selectedTagsProyectType.push(tag);
					this.selectedTag = null;
					this._refreshFilter();

					break;
			}
		}
	}

	removeTag(tag: TagViewModel): void {
		let formControl: FormControl;

		switch (tag.tagType) {
			case this.TAG_TYPE_DEPARTMENT:
				const indexDepartment = this.selectedTagsDepartment.indexOf(
					tag,
					0
				);

				if (indexDepartment > -1) {
					this.selectedTagsDepartment.splice(indexDepartment, 1);
					this._refreshFilter();
				}
				formControl = this.departmentForm;
				break;
			case this.TAG_TYPE_COUNTRY:
				const indexCountry = this.selectedTagsCountry.indexOf(tag, 0);

				if (indexCountry > -1) {
					this.selectedTagsCountry.splice(indexCountry, 1);
					this._refreshFilter();
				}
				formControl = this.countryForm;
				break;
			case this.TAG_TYPE_PROJECT_TYPE:
				const indexProjectType = this.selectedTagsProyectType.indexOf(
					tag,
					0
				);

				if (indexProjectType > -1) {
					this.selectedTagsProyectType.splice(indexProjectType, 1);
					this._refreshFilter();
				}
				formControl = this.projectTypeForm;
				break;
		}

		const formIndex = formControl.value.indexOf(tag.tag);

		if (formIndex > -1) {
			formControl.value.splice(formIndex, 1);
			formControl.setValue(formControl.value);
		}
	}

	// Private methods
	private _loadData() {
		this._loadCebes().subscribe(() => {
			this._loaderService.end();
		});
		this._loadContracts();
		this._loadCentros();
		this._loadKpis();
		this._loadResponsabilidades();
	}

	// Cartera del responsable para la franja superior. Falla en silencio: el
	// bloque simplemente no se muestra (respLoaded solo se activa con éxito).
	private _loadResponsabilidades(): void {
		this._misResponsabilidadesService
			.getMisResponsabilidades()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (d) => {
					this.respContratos = d?.contratos ?? [];
					this.respCentros = d?.centros ?? [];
					this.respLoaded = true;
				},
				error: () => {
					this.respLoaded = false;
				},
			});
	}

	// KPIs globales + serie de actividad mensual (una sola llamada, cacheada en back).
	private _loadKpis(): void {
		this._homeKpisService
			.getKpis()
			.pipe(takeUntil(this.destroy$))
			.subscribe((kpis) => {
				this.kpis = kpis;
				const serie = kpis?.actividadMensual ?? [];
				this.actividadTotal = serie.reduce((s, m) => s + m.numAccesos, 0);
				const max = Math.max(1, ...serie.map((m) => m.numAccesos));
				const fmt = new Intl.DateTimeFormat(
					this._translateService.currentLang || "es",
					{ month: "short" },
				);
				this.actividadBarras = serie.map((m) => ({
					label: fmt.format(new Date(m.anyo, m.mes - 1, 1)).replace(".", ""),
					pct: Math.round((m.numAccesos / max) * 100),
					count: m.numAccesos,
				}));
			});
	}

	// Centros reales (listado) para "Tus centros" y los KPIs del hero.
	// El ribbon se ordena por accesos recientes del usuario (fallback: orden por defecto).
	private _loadCentros(): void {
		this._centrosService
			.getCentros()
			.pipe(takeUntil(this.destroy$))
			.subscribe((centros) => {
				this.centros = centros ?? [];
				this.centrosLoaded = true;
				this._accesosService
					.resumen(TipoElementoAcceso.Centro, 5)
					.pipe(takeUntil(this.destroy$))
					.subscribe((res) => {
						this.centroLastAccess = {};
						res.forEach((r) => {
							this.centroLastAccess[r.elementoId] = r.fechaUltimoAcceso;
						});
						this.centros = this._orderByRecent(
							this.centros,
							res.map((r) => r.elementoId),
							(c) => c.descripcion || c.codigo || "",
						);
						this._loadCardImages();
					});
			});
	}

	// Imagen efectiva (centro → contrato) SOLO de las 5 cards visibles, en una
	// única llamada batch (sin 404 por card en consola). Sin imagen → degradado.
	private _loadCardImages(): void {
		const ids = (this.centros ?? [])
			.slice(0, 5)
			.map((c) => c.id)
			.filter((id) => !!id);
		if (!ids.length) {
			return;
		}
		this._homeKpisService
			.getImagenesCentros(ids)
			.pipe(takeUntil(this.destroy$))
			.subscribe((imgs) => {
				const map: Record<number, string> = {};
				imgs.forEach((i) => {
					if (i.imageDataB64) {
						map[i.centroId] = i.imageDataB64.startsWith("data:")
							? i.imageDataB64
							: `data:image/jpeg;base64,${i.imageDataB64}`;
					}
				});
				this.centroImg = map;
			});
	}

	// Contratos reales (listado) para la tabla "Contratos activos".
	// Se ordenan por accesos recientes del usuario (fallback: orden por defecto).
	private _loadContracts(): void {
		this._contractService
			.getContractsList()
			.pipe(takeUntil(this.destroy$))
			.subscribe((contracts) => {
				const lista = (contracts ?? []) as unknown as ContratoViewModel[];
				this.contratosUsuarioTotal = lista.length;
				// La tabla es "Contratos ACTIVOS": fuera los finalizados y los
				// pendientes de iniciar (según fechas del SP). Sin fechas → se
				// considera activo (no hay evidencia de fin).
				this.contracts = lista.filter((c) => this.isContratoActivo(c));
				this._accesosService
					.resumen(TipoElementoAcceso.Contrato, 6)
					.pipe(takeUntil(this.destroy$))
					.subscribe((res) => {
						this.contractLastAccess = {};
						res.forEach((r) => {
							this.contractLastAccess[r.elementoId] = r.fechaUltimoAcceso;
						});
						this.contracts = this._orderByRecent(
							this.contracts,
							res.map((r) => r.elementoId),
							(c) => c.descripcion || c.codigo || "",
						);
					});
			});
	}

	/**
	 * Ordena: primero los elementos accedidos recientemente (en ese orden de recencia),
	 * y el resto (y los empates) por **orden alfabético** del nombre — que es el fallback
	 * por defecto cuando el usuario no tiene accesos registrados.
	 */
	private _orderByRecent<T extends { id?: number }>(
		items: T[],
		recentIds: number[],
		nameOf: (item: T) => string,
	): T[] {
		const rank = new Map<number, number>();
		recentIds.forEach((id, i) => rank.set(id, i));
		return [...items].sort((a, b) => {
			const ra = a.id != null && rank.has(a.id) ? rank.get(a.id)! : Infinity;
			const rb = b.id != null && rank.has(b.id) ? rank.get(b.id)! : Infinity;
			if (ra !== rb) {
				return ra - rb;
			}
			return (nameOf(a) || "").localeCompare(nameOf(b) || "");
		});
	}

	/**
	 * El backend almacena la fecha con SYSUTCDATETIME() (UTC) pero .NET la serializa
	 * SIN sufijo de zona, por lo que `new Date(str)` la interpretaría como hora LOCAL
	 * del navegador (desfase = offset del cliente; p. ej. CEST UTC+2 → "hace 2h"
	 * cuando es "ahora"). Normalizamos a UTC añadiendo 'Z' si no trae zona.
	 */
	private _asUtcDate(dateString: string): Date {
		const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(dateString.trim());
		return new Date(hasTz ? dateString : `${dateString}Z`);
	}

	/** Texto discreto de "último acceso hace…" (i18n). Vacío si no hay fecha. */
	relativeAccess(dateString?: string): string {
		if (!dateString) {
			return "";
		}
		const ts = this._asUtcDate(dateString).getTime();
		if (isNaN(ts)) {
			return "";
		}
		const min = Math.floor((Date.now() - ts) / 60000);
		if (min < 1) {
			return this._translateService.instant("home.recent.now");
		}
		if (min < 60) {
			return this._translateService.instant("home.recent.min", { n: min });
		}
		const h = Math.floor(min / 60);
		if (h < 24) {
			return this._translateService.instant("home.recent.hours", { n: h });
		}
		const days = Math.floor(h / 24);
		if (days === 1) {
			return this._translateService.instant("home.recent.yesterday");
		}
		if (days < 30) {
			return this._translateService.instant("home.recent.days", { n: days });
		}
		return this._translateService.instant("home.recent.long");
	}

	// Contrato "activo" = iniciado (o sin fecha de inicio) y no finalizado (o sin
	// fecha de fin). Usa las fechas a nivel de listado que devuelve ObtenerContratos.
	isContratoActivo(c: ContratoViewModel): boolean {
		const now = Date.now();
		const ini = c?.contratoInicio ? new Date(c.contratoInicio).getTime() : null;
		const fin = c?.contratoFin ? new Date(c.contratoFin).getTime() : null;
		if (ini && now < ini) {
			return false;
		}
		if (fin && now > fin) {
			return false;
		}
		return true;
	}

	/** Responsable del contrato como EmpleadoLike (avatar EntraID por email). */
	respEmpleado(c: ContratoViewModel): EmpleadoLike {
		return { nombre: c?.responsableNombre, email: c?.responsableEmail };
	}

	/** Nombre del responsable para mostrar (title-case con fallback a email). */
	respNombre(c: ContratoViewModel): string {
		return empleadoLabel(this.respEmpleado(c)) || "—";
	}

	_loadSelectores(): void {
		this._loadDepartments();
		this._loadProjectTypes();
		this._loadCountries();
		this._loadTags();
	}

	private _loadCebes(): Observable<any> {
		const result = this._cebesService.getCebes();

		result
			.pipe(takeUntil(this.destroy$))
			.subscribe((cebes: Array<CebeProcedureViewModel>) => {
				this.allServices = cebes.sort((x, y) =>
					x.actividad > y.actividad ? 1 : -1
				);

				this.allServices.map((service) => {
					service.tags = [
						{
							tag: service.codigoActividad,
							tagType: this.TAG_TYPE_DEPARTMENT,
						} as TagViewModel,
						{
							tag: service.codigoIso2Pais,
							tagType: this.TAG_TYPE_COUNTRY,
						} as TagViewModel,
						{
							tag: service.codigoTipoProyecto,
							tagType: this.TAG_TYPE_PROJECT_TYPE,
						} as TagViewModel,
					];
				});

				this.filteredServices = cebes;

				setTimeout(() => {
					this.loadGlobe = true;
				}, 500);

				return cebes;
			});

		return result;
	}

	private _loadDepartments(): Observable<any> {
		const uniqueDepartments: string[] = Array.from(
			new Set(
				this.allServices
					.filter((cebe) => cebe.codigoActividad != null)
					.map((cebe) => cebe.codigoActividad)
					.sort()
			)
		);

		this.departments = uniqueDepartments;

		return of(uniqueDepartments);
	}

	private _loadProjectTypes(): Observable<any> {
		const uniqueProjectTypes: string[] = Array.from(
			new Set(
				this.allServices
					.filter((cebe) => cebe.codigoTipoProyecto != null)
					.map((cebe) => cebe.codigoTipoProyecto)
					.sort()
			)
		);

		this.projectTypes = uniqueProjectTypes;

		return of(uniqueProjectTypes);
	}

	private _loadCountries(): Observable<any> {
		const uniqueCountries: string[] = Array.from(
			new Set(
				this.allServices
					.filter((cebe) => cebe.codigoIso2Pais != null)
					.map((cebe) => cebe.codigoIso2Pais)
					.sort()
			)
		);

		this.countries = uniqueCountries;

		return of(uniqueCountries);
	}

	private _loadTags(): any {
		const result = [];

		this.tagsDepartment = this.departments
			.map((y: any) => {
				return {
					tag: y,
					tagType: this.TAG_TYPE_DEPARTMENT,
				} as TagViewModel;
			})
			.sort((x, y) => (x.tag > y.tag ? 1 : -1));

		this.tagsCountries = this.countries
			.map((y: any) => {
				return {
					tag: y,
					tagType: this.TAG_TYPE_COUNTRY,
				} as TagViewModel;
			})
			.sort((x, y) => (x.tag > y.tag ? 1 : -1));

		this.tagsProjectType = this.projectTypes
			.map((y: any) => {
				return {
					tag: y,
					tagType: this.TAG_TYPE_PROJECT_TYPE,
				} as TagViewModel;
			})
			.sort((x, y) => (x.tag > y.tag ? 1 : -1));

		return result;
	}

	private _refreshTagList(newSelection: string[], tagType: string) {
		const newTags: TagViewModel[] = newSelection.map((x) => {
			const result: TagViewModel = {
				tag: x,
				tagType,
			};
			return result;
		});

		switch (tagType) {
			case this.TAG_TYPE_DEPARTMENT:
				this.selectedTagsDepartment = newTags;
				break;

			case this.TAG_TYPE_COUNTRY:
				this.selectedTagsCountry = newTags;
				break;

			case this.TAG_TYPE_PROJECT_TYPE:
				this.selectedTagsProyectType = newTags;
				break;

			default:
				newTags.forEach((x) => this.selectedTags.push(x));
				break;
		}

		this._refreshFilter();
	}

	private _refreshFilter() {
		this.filteredServices = [];

		this.filteredServices = this.allServices.filter((service) => {
			return (
				(this.selectedTagsDepartment.some(
					(tag) => tag.tag == service.codigoActividad
				) ||
					this.selectedTagsDepartment.length == 0) &&
				(this.selectedTagsCountry.some(
					(tag) => tag.tag == service.codigoIso2Pais
				) ||
					this.selectedTagsCountry.length == 0) &&
				(this.selectedTagsProyectType.some(
					(tag) => tag.tag == service.codigoTipoProyecto
				) ||
					this.selectedTagsProyectType.length == 0)
			);
		});
		// }

		if (this.filterFavouriteServices) {
			this.filteredServices = this.filteredServices.filter(
				(x) => x.isFav
			);
		}
	}
}
